import React, { useEffect, useState } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Add as AddIcon, Refresh, Visibility, ArrowForward } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import {
    productConversionService,
    ProductConversion,
} from "../../services/productConversionService";
import { formatCurrencyBDT } from "../../utils/currency";
import { ConversionDetailDrawer } from "../../components/conversions/ConversionDetailDrawer";

// total_cost comes back from the API as a JSON string (e.g. "70.00") —
// Number() before summing.
const outputCost = (c: ProductConversion) =>
    c.items.filter((i) => i.role === "OUTPUT").reduce((sum, i) => sum + Number(i.total_cost), 0);

const productNames = (c: ProductConversion, role: "INPUT" | "OUTPUT") =>
    c.items
        .filter((i) => i.role === role)
        .map((i) => i.product?.name || "Product")
        .join(", ");

const ConversionList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    // Phones get cards — the grid needs far more width than a phone has.
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [conversions, setConversions] = useState<ProductConversion[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<ProductConversion | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const loadConversions = () => {
        setLoading(true);
        productConversionService
            .getConversions({ limit: 500 })
            .then(setConversions)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadConversions();
    }, []);

    const handleRowClick = (conversion: ProductConversion) => {
        setSelected(conversion);
        setDrawerOpen(true);
    };

    const columns: GridColDef[] = [
        {
            field: "conversion_number",
            headerName: "Conversion #",
            width: 170,
        },
        {
            field: "conversion_date",
            headerName: "Date",
            width: 170,
            valueFormatter: (value) =>
                value ? new Date(value as string).toLocaleString() : "",
        },
        {
            field: "inputs",
            headerName: "Inputs",
            width: 90,
            sortable: false,
            valueGetter: (_value, row: ProductConversion) =>
                row.items.filter((i) => i.role === "INPUT").length,
        },
        {
            field: "outputs",
            headerName: "Outputs",
            width: 90,
            sortable: false,
            valueGetter: (_value, row: ProductConversion) =>
                row.items.filter((i) => i.role === "OUTPUT").length,
        },
        {
            field: "total_cost",
            headerName: "Total Cost",
            width: 140,
            sortable: false,
            renderCell: (params: GridRenderCellParams<ProductConversion>) => (
                <Typography variant="body2">{formatCurrencyBDT(outputCost(params.row))}</Typography>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: (params: GridRenderCellParams<ProductConversion>) => (
                <Chip label={params.row.status} color="success" size="small" />
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 80,
            sortable: false,
            renderCell: (params: GridRenderCellParams<ProductConversion>) => (
                <Tooltip title="View details">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(params.row);
                        }}
                    >
                        <Visibility fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <Box sx={{ p: { xs: 0, sm: 3 } }}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                mb={3}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                >
                    Product Conversions
                </Typography>
                <Box display="flex" gap={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadConversions} disabled={loading}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() =>
                            navigate("/inventory/conversions/create", {
                                state: { from: location.pathname },
                            })
                        }
                    >
                        New Conversion
                    </Button>
                </Box>
            </Box>

            {isMobile ? (
                loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : conversions.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: "center", py: 6 }}>
                        No conversions yet.
                    </Typography>
                ) : (
                    [...conversions]
                        .sort((a, b) => b.conversion_date.localeCompare(a.conversion_date))
                        .map((c) => (
                            <Card key={c.conversion_id} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
                                {/* The whole card opens the details, like a row click on desktop. */}
                                <CardActionArea onClick={() => handleRowClick(c)}>
                                    <CardContent sx={{ p: 1.75 }}>
                                        <Stack spacing={1}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                                                <Typography variant="body2" fontWeight={700} sx={{ fontFamily: "monospace" }}>
                                                    {c.conversion_number}
                                                </Typography>
                                                <Chip label={c.status} color="success" size="small" />
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(c.conversion_date).toLocaleString()}
                                            </Typography>
                                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 1, alignItems: "start" }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="caption" color="text.secondary">Used</Typography>
                                                    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{productNames(c, "INPUT") || "—"}</Typography>
                                                </Box>
                                                <ArrowForward fontSize="small" color="action" sx={{ mt: 2 }} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="caption" color="text.secondary">Made</Typography>
                                                    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{productNames(c, "OUTPUT") || "—"}</Typography>
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                Total cost {formatCurrencyBDT(outputCost(c))}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))
                )
            ) : (
            <Paper variant="outlined">
                <DataGrid
                    rows={conversions}
                    columns={columns}
                    getRowId={(row) => row.conversion_id}
                    loading={loading}
                    autoHeight
                    rowHeight={56}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25 } },
                        sorting: {
                            sortModel: [{ field: "conversion_date", sort: "desc" }],
                        },
                    }}
                    disableRowSelectionOnClick
                    onRowClick={(params) => handleRowClick(params.row)}
                    sx={{ cursor: "pointer", border: 0 }}
                />
            </Paper>
            )}

            <ConversionDetailDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                conversion={selected}
            />
        </Box>
    );
};

export default ConversionList;
